import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Checkbox, Typography, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';

const { Title, Text } = Typography;

// 背景容器
const BackgroundContainer = styled.div<{ $bgUrl: string }>`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url(${({ $bgUrl }) => $bgUrl}) center/cover no-repeat;
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 1;
  }
`;

// 液体毛玻璃效果登录框
const GlassCard = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 400px;
  padding: 40px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.4),
    inset 0 -1px 0 rgba(255, 255, 255, 0.1);

  /* 液体效果 */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(255, 255, 255, 0.1),
      transparent 70%
    );
    animation: liquid 8s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes liquid {
    0%, 100% {
      transform: translate(0, 0) rotate(0deg);
    }
    25% {
      transform: translate(10px, 10px) rotate(90deg);
    }
    50% {
      transform: translate(-10px, 20px) rotate(180deg);
    }
    75% {
      transform: translate(-20px, -10px) rotate(270deg);
    }
  }
`;

const LoginTitle = styled(Title)`
  &.ant-typography {
    color: #fff;
    text-align: center;
    margin-bottom: 8px !important;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  }
`;

const LoginSubtitle = styled(Text)`
  &.ant-typography {
    color: rgba(255, 255, 255, 0.9);
    text-align: center;
    display: block;
    margin-bottom: 32px;
    text-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 20px;
  }

  .ant-input {
    background: rgba(255, 255, 255, 0.2) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    color: #fff !important;

    &::placeholder {
      color: rgba(255, 255, 255, 0.6) !important;
    }

    &:hover {
      border-color: rgba(255, 255, 255, 0.5) !important;
      background: rgba(255, 255, 255, 0.25) !important;
    }

    &:focus,
    &.ant-input-focused {
      border-color: #fff !important;
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1) !important;
      background: rgba(255, 255, 255, 0.3) !important;
    }
  }

  .ant-input-affix-wrapper {
    background: rgba(255, 255, 255, 0.2) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    color: #fff !important;

    &:hover {
      border-color: rgba(255, 255, 255, 0.5) !important;
      background: rgba(255, 255, 255, 0.25) !important;
    }

    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: #fff !important;
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1) !important;
      background: rgba(255, 255, 255, 0.3) !important;
    }
  }

  .ant-input-affix-wrapper .ant-input {
    background: transparent !important;
    border: none !important;
    color: #fff !important;

    &::placeholder {
      color: rgba(255, 255, 255, 0.6) !important;
    }

    &:hover {
      background: transparent !important;
      border: none !important;
    }

    &:focus {
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
    }
  }

  .ant-input-password-icon,
  .ant-input-suffix .anticon {
    color: rgba(255, 255, 255, 0.7) !important;

    &:hover {
      color: #fff !important;
    }
  }

  .ant-checkbox-wrapper {
    color: rgba(255, 255, 255, 0.9);

    .ant-checkbox {
      .ant-checkbox-inner {
        background: rgba(255, 255, 255, 0.2) !important;
        border-color: rgba(255, 255, 255, 0.4) !important;
      }

      &:hover .ant-checkbox-inner {
        background: rgba(255, 255, 255, 0.3) !important;
        border-color: rgba(255, 255, 255, 0.6) !important;
      }

      &.ant-checkbox-checked .ant-checkbox-inner {
        background: #fff !important;
        border-color: #fff !important;
      }

      &.ant-checkbox-checked .ant-checkbox-inner::after {
        border-color: #667eea !important;
      }
    }
  }

  .ant-form-item-explain-error {
    color: #ff7875 !important;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
`;

const LoginButton = styled(Button)`
  width: 100%;
  height: 44px;
  background: rgba(255, 255, 255, 0.25) !important;
  border: 1px solid rgba(255, 255, 255, 0.4) !important;
  color: #fff !important;
  font-size: 16px;
  font-weight: 600;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.35) !important;
    border-color: rgba(255, 255, 255, 0.6) !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [bgUrl, setBgUrl] = useState('');
  const navigate = useNavigate();

  // 获取带时间戳的背景 URL，避免缓存
  const getBackgroundUrl = () => {
    return `https://api.hoshiroko.com/img/bacg.php?t=${Date.now()}`;
  };

  // 初始化背景
  useEffect(() => {
    setBgUrl(getBackgroundUrl());

    // 检查是否已登录
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/apps');
    }
  }, [navigate]);

  const handleLogin = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch('https://api.liuliyue.cn/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: values.username,
          password: values.password,
        }),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('user_info', JSON.stringify(data.data.user));
        message.success('登录成功');
        navigate('/apps');
      } else {
        // 登录失败，清除旧的 token 并显示错误
        localStorage.removeItem('token');
        localStorage.removeItem('user_info');
        message.error(data.message || '登录失败，请检查账号密码');
      }
    } catch (error: any) {
      // 网络错误或其他异常
      localStorage.removeItem('token');
      localStorage.removeItem('user_info');
      message.error(error?.message || '登录失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BackgroundContainer $bgUrl={bgUrl}>
      <GlassCard>
        <LoginTitle level={2}>琉璃月更新系统</LoginTitle>
        <LoginSubtitle>登录您的账户</LoginSubtitle>

        <StyledForm
          name="login"
          initialValues={{ remember: true }}
          onFinish={handleLogin}
          autoComplete="off"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入邮箱地址' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="邮箱地址"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              size="large"
            />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox style={{ color: 'rgba(255,255,255,0.9)' }}>记住我</Checkbox>
            </Form.Item>
          </div>

          <Form.Item>
            <LoginButton type="primary" htmlType="submit" loading={loading}>
              登录
            </LoginButton>
          </Form.Item>
        </StyledForm>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Text style={{ color: 'rgba(255,255,255,0.8)', marginRight: '8px' }}>
            © 2026 LiuLiYue Team
          </Text>
        </div>
      </GlassCard>
    </BackgroundContainer>
  );
}
